import React from 'react';
import {
  SessionRecord,
  formatSessionAmount,
  formatSessionDate,
  formatSessionTime,
  getSessionStatusColor,
  getSessionTypeName,
} from '@theraply/api-client';

interface SessionDetailsProps {
  session: SessionRecord;
  userType: 'CONSUMER' | 'THERAPIST';
  onClose: () => void;
  onDownloadReceipt?: (sessionId: number) => void;
}

export const SessionDetails: React.FC<SessionDetailsProps> = ({
  session,
  userType,
  onClose,
  onDownloadReceipt,
}) => {
  const statusColor = getSessionStatusColor(session.status);
  const sessionTypeName = getSessionTypeName(session.sessionType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Session Details</h2>
            <p className="text-sm text-gray-500 mt-1">
              Session ID: {session.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Status:</span>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium bg-${statusColor}-100 text-${statusColor}-800`}
            >
              {session.status.replace('_', ' ')}
            </span>
          </div>

          {/* Participants */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Participants
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Client</p>
                <p className="text-base font-medium text-gray-900">
                  {session.consumerName || 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Therapist</p>
                <p className="text-base font-medium text-gray-900">
                  {session.therapistName || 'Unknown'}
                </p>
              </div>
            </div>
          </div>

          {/* Session Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Session Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Type</span>
                <span className="text-sm font-medium text-gray-900">
                  {sessionTypeName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Date</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatSessionDate(session.sessionDate)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Time</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatSessionTime(session.startTime)} -{' '}
                  {formatSessionTime(session.endTime)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Duration</span>
                <span className="text-sm font-medium text-gray-900">
                  {session.durationMinutes} minutes
                </span>
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Payment Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Session Fee</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatSessionAmount(session.amountPaid)}
                </span>
              </div>
              {userType === 'THERAPIST' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Platform Fee</span>
                    <span className="text-sm font-medium text-red-600">
                      -{formatSessionAmount(session.platformFee)}
                    </span>
                  </div>
                  <div className="border-t border-gray-300 pt-3 flex justify-between">
                    <span className="text-base font-semibold text-gray-900">
                      Your Earnings
                    </span>
                    <span className="text-base font-semibold text-green-600">
                      {formatSessionAmount(session.therapistEarnings)}
                    </span>
                  </div>
                </>
              )}
              {session.stripePaymentId && (
                <div className="pt-3 border-t border-gray-300">
                  <p className="text-xs text-gray-500">
                    Payment ID: {session.stripePaymentId}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {session.notes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {session.notes}
              </p>
            </div>
          )}

          {/* Cancellation Info */}
          {session.status === 'CANCELLED' && session.cancellationReason && (
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Cancellation Information
              </h3>
              <p className="text-sm text-red-700">{session.cancellationReason}</p>
              {session.cancelledAt && (
                <p className="text-xs text-red-600 mt-2">
                  Cancelled on:{' '}
                  {new Date(session.cancelledAt).toLocaleString('en-US', {
                    dateStyle: 'long',
                    timeStyle: 'short',
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          {session.hasReceipt && onDownloadReceipt && (
            <button
              onClick={() => onDownloadReceipt(session.id)}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Download Receipt
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
